<script>
import { updateFaviconAndTitle } from "../utils/helpers";

export default {
  name: "PortalProofsPage",
  data() {
    return {
      accountPublicId: this.$route.params.accountPublicId,
      session: null,
      proofs: null,
      loading: true,
      search: "",
    };
  },
  computed: {
    filteredProofs() {
      const proofs = this.proofs || [];
      const term = this.search.trim().toLowerCase();
      if (!term) return proofs;
      return proofs.filter((p) => (p.name || "").toLowerCase().includes(term));
    },
    sections() {
      return [
        { title: "Waiting On You", proofs: this.filteredProofs.filter((p) => p.awaitingYou) },
        { title: "All Proofs", proofs: this.filteredProofs.filter((p) => !p.awaitingYou) },
      ].filter((s) => s.proofs.length > 0);
    },
  },
  async mounted() {
    this.session = this.$A.PortalService.GetSession(this.accountPublicId);
    if (!this.session) {
      this.$router.replace(`/portal/${this.accountPublicId}`);
      return;
    }

    if (this.session.whiteLabelLogoUri) {
      updateFaviconAndTitle({
        accountLogo: this.session.whiteLabelLogoUri,
        accountTitle: this.session.accountName,
      });
    }

    this.$A.PortalService.InstallSession(this.session);
    this.proofs = await this.$A.PortalService.GetProofs();
    if (this.proofs === null) {
      // 401 or server error — treat the session as dead and re-authenticate
      this.$A.PortalService.ClearSession(this.accountPublicId);
      this.$router.replace(`/portal/${this.accountPublicId}?expired`);
      return;
    }
    this.loading = false;
  },
  methods: {
    signOut() {
      this.$A.PortalService.ClearSession(this.accountPublicId);
      this.$router.push(`/portal/${this.accountPublicId}`);
    },
    statusLabel(proof) {
      if (proof.approved) return "Approved";
      if (proof.awaitingYou) return "Awaiting Your Review";
      if (proof.respondedToCurrentVersion) return "Waiting On Others";
      return "In Progress";
    },
    statusClass(proof) {
      if (proof.approved) return "status-approved";
      if (proof.awaitingYou) return "status-awaiting";
      return "status-neutral";
    },
    formatDate(d) {
      return new Date(d).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
};
</script>

<template>
  <div class="portal-page" v-if="session">
    <header class="portal-header">
      <div class="portal-header-brand">
        <img
            v-if="session.whiteLabelLogoUri"
            :src="session.whiteLabelLogoUri"
            class="portal-header-logo"
        />
        <span v-else class="portal-header-title">{{ session.accountName }}</span>
      </div>
      <div class="portal-header-user">
        <span class="portal-header-email">{{ session.approverEmail }}</span>
        <button class="portal-signout" v-on:click="signOut()">Sign Out</button>
      </div>
    </header>

    <div v-if="!loading && proofs && proofs.length > 0" class="portal-subhead">
      <div class="portal-subhead-inner">
        <input
            type="text"
            class="portal-search-input"
            placeholder="Search proofs by name..."
            v-model="search"
        />
        <span class="portal-proof-count">
          {{ filteredProofs.length }} {{ filteredProofs.length === 1 ? "Proof" : "Proofs" }}
        </span>
      </div>
    </div>

    <main class="portal-main">
      <div v-if="loading" class="portal-loading">
        <b-spinner variant="dark"></b-spinner>
      </div>

      <template v-else>
        <div v-if="proofs.length === 0" class="portal-empty">
          <h5>No proofs yet</h5>
          <p>When {{ session.accountName }} sends you a proof, it will show up here.</p>
        </div>

        <div v-if="proofs.length > 0 && filteredProofs.length === 0" class="portal-empty">
          <h5>No proofs match "{{ search.trim() }}"</h5>
          <p>Try a different search.</p>
        </div>

        <section v-for="section in sections" :key="section.title" class="portal-section">
          <h5 class="portal-section-title">{{ section.title }}</h5>
          <div class="portal-grid">
            <a
                v-for="proof in section.proofs"
                :key="proof.id"
                :href="proof.reviewUrl"
                class="portal-card"
                target="_blank"
                rel="noopener"
            >
              <div class="portal-card-thumb">
                <img v-if="proof.thumbnailUri" :src="proof.thumbnailUri" />
                <div v-else class="portal-card-thumb-placeholder">No Preview</div>
              </div>
              <div class="portal-card-body">
                <div class="portal-card-name" :title="proof.name">{{ proof.name }}</div>
                <div class="portal-card-meta">
                  <span class="portal-status" :class="statusClass(proof)">{{ statusLabel(proof) }}</span>
                  <span class="portal-card-date">{{ formatDate(proof.createdAt) }}</span>
                </div>
              </div>
            </a>
          </div>
        </section>
      </template>
    </main>
  </div>
</template>

<style scoped>
.portal-page {
  min-height: 100vh;
  background: #f7f8fa;
}

.portal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.portal-header-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.portal-header-logo {
  max-height: 34px;
  max-width: 140px;
}

.portal-header-title {
  font-weight: 600;
  color: #111827;
}

.portal-header-user {
  display: flex;
  align-items: center;
  gap: 14px;
}

.portal-header-email {
  font-size: 13px;
  color: #6b7280;
}

.portal-signout {
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
}

.portal-signout:hover {
  background: #f3f4f6;
}

.portal-main {
  max-width: 1080px;
  margin: 0 auto;
  padding: 32px 24px;
}

.portal-loading,
.portal-empty {
  text-align: center;
  padding: 80px 0;
  color: #6b7280;
}

.portal-subhead {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 12px 24px;
}

.portal-subhead-inner {
  max-width: 1080px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.portal-proof-count {
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
}

.portal-search-input {
  width: 100%;
  max-width: 420px;
  padding: 9px 14px;
  font-size: 14px;
  color: #374151;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  outline: none;
}

.portal-search-input:focus {
  border-color: #019255;
}

.portal-section {
  margin-bottom: 36px;
}

.portal-section-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 14px;
}

.portal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 18px;
}

.portal-card {
  display: block;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  text-decoration: none;
  transition: box-shadow 0.15s ease;
}

.portal-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  text-decoration: none;
}

.portal-card-thumb {
  height: 140px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.portal-card-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.portal-card-thumb-placeholder {
  font-size: 13px;
  color: #9ca3af;
}

.portal-card-body {
  padding: 12px 14px;
}

.portal-card-name {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.portal-card-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.portal-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
}

.status-approved {
  background: #ecfdf5;
  color: #047857;
}

.status-awaiting {
  background: #fffbeb;
  color: #b45309;
}

.status-neutral {
  background: #f3f4f6;
  color: #4b5563;
}

.portal-card-date {
  font-size: 12px;
  color: #9ca3af;
}
</style>
